import React from 'react';
import { CoryMessageContent } from '../common/CoryMessageContent';
import { messageAvatarGradient, emptyStateGradient } from './coryStyles';
import { CoryUpgradeTeaser } from './CoryUpgradeTeaser';

const SUGGESTED_PROMPTS = [
  'How is my business performing?',
  'What modules are available?',
  'I need support',
];

// Time-of-day greeting from the user's own device clock.
const timeGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

// Role-aware opening prompts so Cory feels like it knows the person it's
// talking to. Falls back to the generic set.
const promptsForRoles = (roles = []) => {
  const r = (roles || []).map((x) => String(x).toLowerCase());
  const has = (...keys) => keys.some((k) => r.some((role) => role.includes(k)));
  if (has('owner', 'admin', 'super')) {
    return ['How is my business performing?', 'How is my team tracking?', 'What should I focus on this week?'];
  }
  if (has('finance', 'account')) {
    return ['How is our cashflow looking?', 'Any cost anomalies this month?', 'What modules are available?'];
  }
  if (has('sales')) {
    return ['How is my pipeline tracking?', 'Which deals need attention?', 'I need support'];
  }
  if (has('manage')) {
    return ['How is my team tracking?', 'How is my business performing?', 'I need support'];
  }
  return SUGGESTED_PROMPTS;
};

const CoryAvatar = () => (
  <div
    className="w-6 h-6 rounded-lg mr-2 mt-0.5 shrink-0 flex items-center justify-center"
    style={messageAvatarGradient}
  >
    <span className="text-white text-[10px] font-bold">C</span>
  </div>
);

const EmptyState = ({ firstName, roles, status, onUpgrade, onPromptClick }) => {
  const prompts = promptsForRoles(roles);
  return (
  <div className="flex flex-col items-center justify-center h-full text-center px-6 py-4">
    <div
      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
      style={emptyStateGradient}
    >
      <span
        className="text-2xl font-bold text-slate-700"
        style={{ textShadow: '0 0 16px rgba(99,102,241,0.2)' }}
      >
        C
      </span>
    </div>
    <h4 className="text-sm font-semibold text-slate-800 mb-1" data-testid="cory-greeting">{timeGreeting()}, {firstName}</h4>
    <p className="text-xs text-slate-400 leading-relaxed max-w-[260px]">
      I&apos;m Cory — your Corvana AI assistant. Ask me about your business, navigate the platform,
      or get support.
    </p>
    <div className="mt-5 flex flex-wrap gap-2 justify-center">
      {prompts.map((q, i) => (
        <button
          key={q}
          data-testid={`cory-suggestion-${i}`}
          onClick={() => onPromptClick(q)}
          className="text-[11px] px-3.5 py-2 rounded-full bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 border border-slate-200/60 hover:border-indigo-200 transition-all duration-200"
        >
          {q}
        </button>
      ))}
    </div>
    {status && !status.is_paid && (
      <div className="mt-6 w-full max-w-[300px]">
        <CoryUpgradeTeaser status={status} onUpgrade={onUpgrade} />
      </div>
    )}
  </div>
  );
};

const MessageBubble = ({ msg, msgKey, onAddModule, onAddBundle, onDeflectionOutcome, deflectionAnswered }) => (
  <div key={msgKey} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
    {msg.role === 'assistant' && <CoryAvatar />}
    <div
      className={`max-w-[80%] px-3.5 py-2.5 text-[13px] leading-relaxed ${
        msg.role === 'user'
          ? 'bg-slate-800 text-white rounded-2xl rounded-br-md shadow-sm whitespace-pre-wrap'
          : 'bg-slate-100/80 text-slate-800 rounded-2xl rounded-bl-md border border-slate-200/60'
      }`}
    >
      {msg.role === 'assistant'
        ? <CoryMessageContent content={msg.content} sources={msg.sources} />
        : msg.content}

      {/* Ticket deflection — Cory matched a known issue and suggests a fix
          before raising a ticket. "Did this solve it?" */}
      {msg.deflection && (
        <div className="mt-2.5 pt-2 border-t border-slate-200/60" data-testid="cory-deflection-card">
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
            <p className="text-[11px] font-semibold text-amber-800 mb-1">💡 This looks like a known issue: {msg.deflection.title}</p>
            <p className="text-[12px] text-slate-700 leading-snug">{msg.deflection.suggested_fix}</p>
            {!deflectionAnswered?.has(msg.deflection.issue_id) ? (
              <div className="flex items-center gap-2 mt-2.5">
                <button
                  onClick={() => onDeflectionOutcome?.(msg.deflection.issue_id, true)}
                  data-testid="cory-deflection-resolved"
                  className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                >
                  This fixed it 🎉
                </button>
                <button
                  onClick={() => onDeflectionOutcome?.(msg.deflection.issue_id, false)}
                  data-testid="cory-deflection-unresolved"
                  className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Still need help
                </button>
              </div>
            ) : (
              <p className="text-[10px] text-slate-400 mt-2" data-testid="cory-deflection-answered">Thanks for the feedback.</p>
            )}
          </div>
        </div>
      )}

      {/* One-click module upsell — answers exactly what they asked, addable now */}
      {msg.upsell_modules?.length > 0 && (
        <div className="mt-2.5 pt-2 border-t border-slate-200/60 space-y-2" data-testid="cory-upsell">
          <p className="text-[10px] text-slate-400">Unlock this with a module:</p>
          {msg.upsell_modules.map((mod) => (
            <div key={mod.module_id} className="flex items-center gap-2 rounded-xl bg-white border border-indigo-100 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-indigo-200">
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-slate-800 truncate">{mod.name}</p>
                <p className="text-[10px] text-slate-500 truncate">{mod.description}</p>
              </div>
              <button
                onClick={() => onAddModule?.(mod)}
                data-testid={`cory-add-module-${mod.module_id}`}
                className="shrink-0 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
              >
                Add · ${mod.price_monthly}/mo
              </button>
            </div>
          ))}
          {msg.upsell_modules.length > 1 && (
            <button
              onClick={() => onAddBundle?.(msg.upsell_modules)}
              data-testid="cory-add-bundle"
              className="w-full text-[11px] font-semibold px-2.5 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-md transition-[box-shadow,transform] hover:-translate-y-0.5"
            >
              Add all together · ${msg.upsell_modules.reduce((s, m) => s + (m.price_monthly || 0), 0)}/mo
            </button>
          )}
        </div>
      )}

      {msg.limit_reached && (
        <p className="mt-2 text-[10px] text-amber-600" data-testid="cory-limit-note">
          Monthly Cory limit reached on the {msg.tier} plan — tech help stays free anytime.
        </p>
      )}

      {msg.suggested_modules?.length > 0 && !msg.upsell_modules?.length && (
        <div className="mt-2.5 pt-2 border-t border-slate-200/60">
          <p className="text-[10px] text-slate-400 mb-1.5">Related modules:</p>
          <div className="flex flex-wrap gap-1">
            {msg.suggested_modules.map((m) => (
              <span
                key={m}
                className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full border border-indigo-100"
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

const TypingIndicator = () => (
  <div className="flex justify-start">
    <CoryAvatar />
    <div className="bg-slate-100/80 border border-slate-200/60 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5">
      {[0, 1, 2].map((j) => (
        <span
          key={j}
          className="w-1.5 h-1.5 rounded-full bg-slate-400"
          style={{ animation: 'coryDot 1.4s infinite ease-in-out both', animationDelay: `${j * 0.16}s` }}
        />
      ))}
    </div>
  </div>
);

export const CoryMessageList = ({ messages, loading, firstName, roles, status, onUpgrade, onPromptClick, onAddModule, onAddBundle, onDeflectionOutcome, deflectionAnswered, scrollRef }) => (
  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="cory-messages">
    {messages.length === 0 && <EmptyState firstName={firstName} roles={roles} status={status} onUpgrade={onUpgrade} onPromptClick={onPromptClick} />}
    {messages.map((msg, i) => (
      <MessageBubble
        key={msg.timestamp ? `${msg.timestamp}-${i}` : i}
        msg={msg}
        onAddModule={onAddModule}
        onAddBundle={onAddBundle}
        onDeflectionOutcome={onDeflectionOutcome}
        deflectionAnswered={deflectionAnswered}
      />
    ))}
    {/* When an entry-plan user is running low (or out), keep the upgrade nudge
        in view at the foot of the conversation too. */}
    {messages.length > 0 && status && !status.is_paid &&
      typeof status.remaining === 'number' && status.remaining <= 5 && (
      <div className="pt-1">
        <CoryUpgradeTeaser status={status} onUpgrade={onUpgrade} />
      </div>
    )}
    {loading && <TypingIndicator />}
    <div ref={scrollRef} />
  </div>
);
